import type { IconKind, SidebarNav } from '@elva/ui';

export type GrowthModuleId = 'ads' | 'local' | 'seo' | 'ops';

export interface GrowthModule {
  id: GrowthModuleId;
  label: string;
  subtitle: string;
  icon: IconKind;
  defaultPath: string;
  nav: SidebarNav[];
}

const ADS_NAV: SidebarNav[] = [
  { key: 'campaigns', icon: 'target', label: 'Ad Campaigns' },
  { key: 'suggested', icon: 'sparkle', label: 'Suggested Campaigns' },
  { key: 'leads', icon: 'team', label: 'Lead Inbox' },
  { key: 'calltracking', icon: 'phone', label: 'Call Tracking' },
  { key: 'landingpages', icon: 'rocket', label: 'Landing Pages' },
];

const LOCAL_NAV: SidebarNav[] = [
  { key: 'localmarket', icon: 'pin', label: 'Local Market Intelligence' },
  { key: 'market', icon: 'globe', label: 'Market Overview' },
  { key: 'competitors', icon: 'eye', label: 'Competitor Intelligence' },
  { key: 'gbp', icon: 'home', label: 'Google Page' },
  { key: 'gbpphotos', icon: 'image', label: 'Google Page Photos' },
  { key: 'mappack', icon: 'pin', label: 'Google Map Intelligence' },
  { key: 'citations', icon: 'link', label: 'Local Citations' },
];

const SEO_NAV: SidebarNav[] = [
  { key: 'websitehealth', icon: 'pulse', label: 'Website Health' },
  { key: 'keywords', icon: 'activity', label: 'Keyword Rankings' },
  { key: 'content', icon: 'doc', label: 'Content Articles' },
  { key: 'schema', icon: 'list', label: 'Schema Markup' },
];

const OPS_NAV: SidebarNav[] = [
  { key: 'dashboard', icon: 'grid', label: 'Marketing Dashboard' },
  { key: 'approvals', icon: 'check-circ', label: 'Approval Queue' },
  { key: 'autopilotlog', icon: 'bolt', label: 'Autopilot Log' },
  { key: 'monthlyreport', icon: 'calendar', label: 'Monthly Report' },
  { key: 'settings', icon: 'cog', label: 'Settings' },
  { key: 'setupchecklist', icon: 'clipboard', label: 'Setup Checklist' },
];

export const GROWTH_MODULES: GrowthModule[] = [
  {
    id: 'local',
    label: 'Market Intelligence',
    subtitle: 'Local market, Google Page & maps',
    icon: 'pin',
    defaultPath: '/local-market-intelligence',
    nav: LOCAL_NAV,
  },
  {
    id: 'ads',
    label: 'Google Ads',
    subtitle: 'Campaigns, leads & landing pages',
    icon: 'target',
    defaultPath: '/campaigns',
    nav: ADS_NAV,
  },
  {
    id: 'seo',
    label: 'Website SEO',
    subtitle: 'Health, rankings & content',
    icon: 'doc',
    defaultPath: '/website-health',
    nav: SEO_NAV,
  },
  {
    id: 'ops',
    label: 'Operations',
    subtitle: 'Dashboard, automation & settings',
    icon: 'grid',
    defaultPath: '/dashboard',
    nav: OPS_NAV,
  },
];

/** Flat list of every sidebar item across modules (for labels / lookups). */
export const SIDEBAR_NAV: SidebarNav[] = GROWTH_MODULES.flatMap((module) => module.nav);

export const NAV_PATHS: Record<string, string> = {
  localmarket: '/local-market-intelligence',
  market: '/market',
  competitors: '/competitors',
  gbp: '/gbp',
  gbpphotos: '/gbp/photos',
  mappack: '/map-pack',
  citations: '/citations',
  campaigns: '/campaigns',
  suggested: '/suggested-campaigns',
  leads: '/leads',
  calltracking: '/call-tracking',
  landingpages: '/landing-pages',
  keywords: '/keywords',
  content: '/content',
  websitehealth: '/website-health',
  schema: '/schema',
  dashboard: '/dashboard',
  approvals: '/approval-queue',
  autopilotlog: '/autopilot-log',
  monthlyreport: '/monthly-report',
  settings: '/settings',
  setupchecklist: '/setup-checklist',
};

/** App entry — first impression lands on Local Market. */
export const APP_HOME_PATH = NAV_PATHS.localmarket!;

const NAV_PATH_ENTRIES = Object.entries(NAV_PATHS).sort(
  (a, b) => b[1].length - a[1].length,
);

function pathMatches(pathname: string, path: string): boolean {
  if (pathname === path) return true;
  if (path === '/') return false;
  return pathname.startsWith(`${path}/`);
}

export function getNavKeyFromPath(pathname: string): string {
  if (pathname === '/') return 'localmarket';
  // Article detail lives under /articles/:id but sidebar key is "content"
  if (pathname.startsWith('/articles/')) return 'content';
  const match = NAV_PATH_ENTRIES.find(([, path]) => pathMatches(pathname, path));
  return match?.[0] ?? 'localmarket';
}

export function getModuleById(id: GrowthModuleId): GrowthModule {
  const module = GROWTH_MODULES.find((item) => item.id === id);
  if (!module) {
    return GROWTH_MODULES[0]!;
  }
  return module;
}

export function getModuleNav(id: GrowthModuleId): SidebarNav[] {
  return getModuleById(id).nav;
}

export function getModuleIdFromPath(pathname: string): GrowthModuleId {
  const navKey = getNavKeyFromPath(pathname);
  const module = GROWTH_MODULES.find((item) =>
    item.nav.some((navItem) => navItem.key === navKey),
  );
  return module?.id ?? 'local';
}

export function getModuleFromPath(pathname: string): GrowthModule {
  return getModuleById(getModuleIdFromPath(pathname));
}
